import { Injectable } from '@angular/core';
import {
  generateLifePlanInsurance,
  generateLifePlanInsurances,
} from '@core/models/insurance.model';
import {
  deepCloneLifePlan,
  LifePlan,
  updateLifePlanInsuranceTargetsCurrentProductsWithLiteValuesAndLimits,
} from '@core/models/life-plan.model';
import { setRecommendedProducts } from '@core/models/products-per-insurance-target.model';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import { throwError } from 'rxjs';
import { catchError, map, switchMap, tap, toArray } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { LifePlanService } from './life-plan.service';
import { LiteService } from './lite.service';
import { ReportsService } from './reports.service';

@Injectable({
  providedIn: 'root',
})
export class LifePlanManagerService {
  private managedLifePlan!: LifePlan;
  private clientId!: string;

  get lifePlan() {
    return deepCloneLifePlan(this.managedLifePlan);
  }

  constructor(
    private lifePlanService: LifePlanService,
    private store: Store<AppState>,
    private reportsService: ReportsService,
    private liteService: LiteService,
    private configService: ConfigService
  ) {
    this.store.subscribe({
      next: (state) => {
        this.managedLifePlan = state.lifePlan.currentLifePlan;
        this.clientId = state.clientData.clientId;
      },
    });
  }

  createAndManageAgentLifePlan(lifePlan: LifePlan) {
    return this.lifePlanService.createAgentLifePlan(lifePlan).pipe(
      tap({
        next: (newLifePlan) =>
          this.store.dispatch(actions.fullUpdateLifePlan({ newLifePlan })),
      })
    );
  }

  getAndManageLifePlan(lifePlanId: string) {
    return this.lifePlanService.getLifePlan(lifePlanId, this.clientId).pipe(
      tap({
        next: (newLifePlan) =>
          this.store.dispatch(actions.fullUpdateLifePlan({ newLifePlan })),
      })
    );
  }

  updateAndManageLifePlan(lifePlan: LifePlan) {
    return this.lifePlanService.updateLifePlan(lifePlan).pipe(
      tap({
        next: (newLifePlan) =>
          this.store.dispatch(actions.fullUpdateLifePlan({ newLifePlan })),
      })
    );
  }

  generateLifePlanInsuranceTargetsAndPopulateThemWithProducts(
    lifePlan: LifePlan,
    scope?: 'all' | string
  ) {
    const lifePlanClon = deepCloneLifePlan(lifePlan);
    if (!lifePlanClon.insurances) {
      if (lifePlanClon.type === 'COMPLETE') {
        lifePlanClon.insurances = generateLifePlanInsurances(
          lifePlanClon,
          this.configService.config
        );
      } else {
        lifePlanClon.insurances = generateLifePlanInsurance(
          lifePlanClon,
          this.configService.config
        );
      }
    }
    return this.reportsService.getAndUpdateReportsData(lifePlanClon).pipe(
      map((reportsData) => {
        setRecommendedProducts(
          lifePlanClon,
          reportsData.productsPerInsuranceTarget,
          scope
        );
        return lifePlanClon;
      }),
      switchMap((lifePlanWithRecommendations) => {
        return this.liteService
          .calculateAllTargetsWithLimitsFromLifePlan(
            lifePlanWithRecommendations
          )
          .pipe(
            toArray(),
            map((responses) => {
              updateLifePlanInsuranceTargetsCurrentProductsWithLiteValuesAndLimits(
                lifePlanWithRecommendations,
                responses
              );
              return lifePlanWithRecommendations;
            })
          );
      }),
      catchError((err) => {
        console.error(err);
        return throwError(err);
      })
    );
  }
}
